import * as clientDb from "../db/clientDb";
import type { LocalItem, LocalLocation, GroceryItem } from "../db/clientDb";

export type MutationType =
    | "create_item"
    | "update_item"
    | "delete_item"
    | "decrement_item"
    | "create_location"
    | "update_location"
    | "delete_location"
    | "create_grocery"
    | "update_grocery"
    | "delete_grocery";

function newId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function generateTempId(): number {
    return -Math.floor(Math.random() * 1_000_000_000);
}

export async function createItem(fields: Omit<LocalItem, "id" | "version" | "updated_at" | "field_timestamps">, image?: Blob): Promise<LocalItem> {
    const tempId = generateTempId();
    const now = new Date().toISOString();
    const item: LocalItem = {
        ...fields,
        id: tempId,
        version: 0,
        field_timestamps: {},
        updated_at: now,
        created_at: fields.created_at ?? now,
    };
    await clientDb.putItem(item);
    const mutation = await clientDb.addMutation({
        type: "create_item",
        table: "items",
        recordId: tempId,
        fields: item as unknown as Record<string, unknown>,
        clientTimestamp: now,
    });
    if (image) {
        await clientDb.storePendingImage(newId(), mutation.id, image, "item");
    }
    return item;
}

export async function updateItem(id: number, fields: Partial<LocalItem>, image?: Blob): Promise<LocalItem> {
    const existing = await clientDb.getItem(id);
    if (!existing) throw new Error(`Item ${id} not found locally`);
    const now = new Date().toISOString();
    const updated = { ...existing, ...fields, id: existing.id, updated_at: now };
    await clientDb.putItem(updated);

    const mutation = await clientDb.addMutation({
        type: "update_item",
        table: "items",
        recordId: id,
        fields: { ...fields } as Record<string, unknown>,
        clientTimestamp: now,
    });
    if (image) {
        await clientDb.storePendingImage(newId(), mutation.id, image, "item");
    }
    return updated;
}

export async function deleteItem(id: number): Promise<void> {
    await clientDb.deleteItemLocal(id);
    await clientDb.addMutation({
        type: "delete_item",
        table: "items",
        recordId: id,
        fields: {},
        clientTimestamp: new Date().toISOString(),
    });
}

export async function decrementItem(id: number): Promise<LocalItem> {
    const existing = await clientDb.getItem(id);
    if (!existing) throw new Error(`Item ${id} not found locally`);
    const now = new Date().toISOString();
    const updated = {
        ...existing,
        quantity: Math.max(0, existing.quantity - 1),
        updated_at: now,
    };
    await clientDb.putItem(updated);
    await clientDb.addMutation({
        type: "decrement_item",
        table: "items",
        recordId: id,
        fields: { quantity: updated.quantity, updated_at: updated.updated_at },
        clientTimestamp: now,
    });
    return updated;
}

export async function createLocation(fields: Partial<Omit<LocalLocation, "id" | "version" | "field_timestamps">>, image?: Blob): Promise<LocalLocation> {
    const tempId = generateTempId();
    const now = new Date().toISOString();
    const location: LocalLocation = {
        name: fields.name ?? "",
        image_path: fields.image_path ?? null,
        sort_order: fields.sort_order ?? 0,
        id: tempId,
        version: 0,
        field_timestamps: {},
        created_at: fields.created_at ?? now,
    };
    await clientDb.putLocation(location);
    const mutation = await clientDb.addMutation({
        type: "create_location",
        table: "locations",
        recordId: tempId,
        fields: location as unknown as Record<string, unknown>,
        clientTimestamp: now,
    });
    if (image) {
        await clientDb.storePendingImage(newId(), mutation.id, image, "location");
    }
    return location;
}

export async function updateLocation(id: number, fields: Partial<LocalLocation>, image?: Blob): Promise<LocalLocation> {
    const existing = await clientDb.getLocation(id);
    if (!existing) throw new Error(`Location ${id} not found locally`);
    const now = new Date().toISOString();
    const updated = { ...existing, ...fields, id: existing.id };
    await clientDb.putLocation(updated);

    const mutation = await clientDb.addMutation({
        type: "update_location",
        table: "locations",
        recordId: id,
        fields: { ...fields } as Record<string, unknown>,
        clientTimestamp: now,
    });
    if (image) {
        await clientDb.storePendingImage(newId(), mutation.id, image, "location");
    }
    return updated;
}

export async function deleteLocation(id: number): Promise<void> {
    await clientDb.deleteLocationLocal(id);
    await clientDb.addMutation({
        type: "delete_location",
        table: "locations",
        recordId: id,
        fields: {},
        clientTimestamp: new Date().toISOString(),
    });
}

export async function createGrocery(fields: Partial<Omit<GroceryItem, "id" | "version" | "field_timestamps">>): Promise<GroceryItem> {
    const tempId = generateTempId();
    const now = new Date().toISOString();
    const grocery: GroceryItem = {
        id: tempId,
        name: fields.name ?? "",
        item_id: fields.item_id ?? null,
        min_quantity: fields.min_quantity ?? null,
        mode: fields.mode ?? "manual",
        stocked: fields.stocked ?? false,
        notes: fields.notes ?? null,
        version: 0,
        field_timestamps: {},
        created_at: fields.created_at ?? now,
    };
    await clientDb.putGroceryItem(grocery);
    await clientDb.addMutation({
        type: "create_grocery",
        table: "grocery_items",
        recordId: tempId,
        fields: grocery as unknown as Record<string, unknown>,
        clientTimestamp: now,
    });
    return grocery;
}

export async function updateGrocery(id: number, fields: Partial<GroceryItem>): Promise<GroceryItem> {
    const existing = await clientDb.getGroceryItem(id);
    if (!existing) throw new Error(`Grocery ${id} not found locally`);
    const updated = { ...existing, ...fields, id: existing.id };
    await clientDb.putGroceryItem(updated);

    await clientDb.addMutation({
        type: "update_grocery",
        table: "grocery_items",
        recordId: id,
        fields: { ...fields } as Record<string, unknown>,
        clientTimestamp: new Date().toISOString(),
    });
    return updated;
}

export async function deleteGrocery(id: number): Promise<void> {
    await clientDb.deleteGroceryItemLocal(id);
    await clientDb.addMutation({
        type: "delete_grocery",
        table: "grocery_items",
        recordId: id,
        fields: {},
        clientTimestamp: new Date().toISOString(),
    });
}
